import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getSeriesDetails, getSeasonDetails, getImageUrl } from '../services/api';

const SeriesContainer = styled.div`
  padding-top: 70px;
  min-height: 100vh;
`;

const Backdrop = styled.div`
  height: 70vh;
  position: relative;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.8)),
    url(${props => props.background}) center/cover no-repeat;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 40px;
`;

const Poster = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
`;

const Info = styled.div`
  color: white;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 1.5rem;
  color: #b3b3b3;
`;

const Overview = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const SeasonSelector = styled.div`
  margin: 2rem 0;
`;

const SeasonButton = styled.button`
  background: ${props => props['data-active'] ? '#e50914' : 'transparent'};
  color: white;
  border: 1px solid ${props => props['data-active'] ? '#e50914' : '#333'};
  padding: 8px 16px;
  margin-right: 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props['data-active'] ? '#e50914' : '#333'};
  }
`;

const EpisodesList = styled.div`
  margin-top: 2rem;
`;

const Episode = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 20px;
  margin-bottom: 2rem;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const EpisodeImage = styled.img`
  width: 100%;
  border-radius: 4px;
`;

const EpisodeInfo = styled.div`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #b3b3b3;
    margin-bottom: 1rem;
  }
`;

const SeriesDetails = () => {
  const { id } = useParams();
  const [series, setSeries] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [seasonDetails, setSeasonDetails] = useState(null);

  useEffect(() => {
    const fetchSeriesDetails = async () => {
      try {
        const data = await getSeriesDetails(id);
        setSeries(data);
      } catch (error) {
        console.error('Error fetching series details:', error);
      }
    };

    fetchSeriesDetails();
  }, [id]);

  useEffect(() => {
    const fetchSeasonDetails = async () => {
      if (!series) return;
      try {
        const data = await getSeasonDetails(id, selectedSeason);
        setSeasonDetails(data);
      } catch (error) {
        console.error('Error fetching season details:', error);
      }
    };

    fetchSeasonDetails();
  }, [id, selectedSeason, series]);

  if (!series) return null;

  return (
    <SeriesContainer>
      <Backdrop background={getImageUrl(series.backdrop_path, 'original')} />
      <Content>
        <Poster src={getImageUrl(series.poster_path, 'w500')} alt={series.name} />
        <Info>
          <Title>{series.name}</Title>
          <MetaInfo>
            <span>{series.first_air_date?.split('-')[0]}</span>
            <span>{series.number_of_seasons} Seasons</span>
            <span>{series.vote_average.toFixed(1)}/10</span>
          </MetaInfo>
          <Overview>{series.overview}</Overview>

          <SeasonSelector>
            {Array.from({ length: series.number_of_seasons }, (_, i) => i + 1).map(seasonNum => (
              <SeasonButton
                key={seasonNum}
                data-active={selectedSeason === seasonNum}
                onClick={() => setSelectedSeason(seasonNum)}
              >
                Season {seasonNum}
              </SeasonButton>
            ))}
          </SeasonSelector>

          {seasonDetails && (
            <EpisodesList>
              <h2>Season {selectedSeason}</h2>
              {seasonDetails.episodes.map(episode => (
                <Episode key={episode.id}>
                  <EpisodeImage
                    src={getImageUrl(episode.still_path, 'w300')}
                    alt={episode.name}
                  />
                  <EpisodeInfo>
                    <h3>{episode.episode_number}. {episode.name}</h3>
                    <p>{episode.air_date}</p>
                    <p>{episode.overview}</p>
                  </EpisodeInfo>
                </Episode>
              ))}
            </EpisodesList>
          )}
        </Info>
      </Content>
    </SeriesContainer>
  );
};

export default SeriesDetails; 